<template>
  <div class="content-wrapper view-queue-list p-0">
    <div class="content ql-page">
      <div class="card ql-card">
        <!-- ===== Filter bar (styled like DF Correction) ===== -->
        <div class="filter-bar">
          <div class="filter-grid filter-grid-top">
            <!-- Date + day navigation -->
            <div class="field">
              <label><i class="fas fa-calendar"></i> วันที่</label>
              <div class="ql-date-row">
                <button
                  type="button"
                  class="icon-btn"
                  title="วันก่อนหน้า"
                  @click="prevDay"
                >
                  <i class="fas fa-chevron-left"></i>
                </button>
                <div class="date-wrap">
                  <datetime
                    type="date"
                    :value="date"
                    input-class="ql-date-input"
                    format="ccc dd LLL yyyy"
                    value-zone="UTC+7"
                    :phrases="{ ok: 'ตกลง', cancel: 'ยกเลิก' }"
                    :week-start="7"
                    auto
                    @input="formatDate"
                  ></datetime>
                </div>
                <button
                  type="button"
                  class="icon-btn"
                  title="วันถัดไป"
                  @click="nextDay"
                >
                  <i class="fas fa-chevron-right"></i>
                </button>
              </div>
            </div>

            <!-- Quick day chips -->
            <div class="field">
              <label>&nbsp;</label>
              <div class="quick-chips">
                <span
                  class="chip"
                  :class="{ active: isSameDate(-1) }"
                  @click="yesterday"
                  >เมื่อวาน</span
                >
                <span
                  class="chip"
                  :class="{ active: isSameDate(0) }"
                  @click="today"
                  >วันนี้</span
                >
                <span
                  class="chip"
                  :class="{ active: isSameDate(1) }"
                  @click="tomorrow"
                  >พรุ่งนี้</span
                >
              </div>
            </div>

            <!-- Search -->
            <div class="field">
              <label><i class="fas fa-search"></i> ค้นหา</label>
              <div class="ctrl-wrap">
                <i class="fas fa-search lead"></i>
                <input
                  class="ctrl"
                  type="search"
                  v-model.trim="filter"
                  placeholder="ชื่อคนไข้, DN หรือเบอร์โทร"
                />
                <i
                  v-if="filter"
                  class="fas fa-times-circle trail clearable"
                  @click="filter = ''"
                ></i>
              </div>
            </div>

            <!-- Export -->
            <div class="field actions">
              <b-dropdown size="sm" variant="outline-success" right>
                <template #button-content>
                  <i class="fas fa-download"></i> ส่งออก
                </template>
                <b-dropdown-item @click="exportExcel">
                  <i class="fas fa-file-excel mr-2"></i>Excel
                </b-dropdown-item>
                <b-dropdown-item @click="generatePdf('queueListTable')">
                  <i class="fas fa-file-pdf mr-2"></i>PDF
                </b-dropdown-item>
              </b-dropdown>
            </div>
          </div>

          <!-- The 4 existing filters -->
          <div class="filter-grid filter-grid-filters">
            <div class="field">
              <label><i class="fas fa-user-md"></i> แพทย์</label>
              <div class="ctrl-wrap select-wrap">
                <select class="ctrl" v-model="filterDoctor">
                  <option :value="null">ทั้งหมด</option>
                  <option v-for="d in doctorList" :key="d.uid" :value="d.uid">
                    {{ d.fullName }}
                  </option>
                </select>
              </div>
            </div>
            <div class="field">
              <label><i class="fas fa-door-open"></i> ห้อง</label>
              <div class="ctrl-wrap select-wrap">
                <select class="ctrl" v-model="filterRoom">
                  <option :value="null">ทั้งหมด</option>
                  <option v-for="r in roomList" :key="r.id" :value="r.id">
                    {{ r.title }}
                  </option>
                </select>
              </div>
            </div>
            <div class="field">
              <label><i class="fas fa-bell"></i> แจ้งเตือน</label>
              <div class="ctrl-wrap select-wrap">
                <select class="ctrl" v-model="filterReminder">
                  <option :value="null">ทั้งหมด</option>
                  <option value="incompleted">แจ้งเตือนงาน (ยังไม่เสร็จ)</option>
                  <option value="completed">แจ้งเตือนงาน (เสร็จแล้ว)</option>
                </select>
              </div>
            </div>
            <div class="field">
              <label><i class="fas fa-flag"></i> สถานะ</label>
              <div class="ctrl-wrap select-wrap">
                <select class="ctrl" v-model="filterStatus">
                  <option :value="null">ทั้งหมด</option>
                  <option
                    v-for="s in getAppointmentStatusList"
                    :key="s.code"
                    :value="s.code"
                  >
                    {{ s.label }}
                  </option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <!-- Table toolbar: count + clear -->
        <div class="table-toolbar">
          <div class="left">
            พบ <strong>{{ filteredAppointmentList.length }}</strong> นัด
          </div>
          <div class="right">
            <button
              v-if="
                filterDoctor ||
                filterRoom ||
                filterReminder ||
                filterStatus ||
                filter
              "
              type="button"
              class="btn btn-secondary btn-xs"
              @click="defaultValue"
            >
              <i class="fas fa-undo"></i> ล้างตัวกรอง
            </button>
          </div>
        </div>

        <!-- ===== Table ===== -->
        <div class="tbl-wrap">
          <b-table
            ref="queueListTable"
            class="ql-tbl my-0"
            small
            hover
            head-variant="light"
            sort-icon-left
            :items="filteredAppointmentList"
            :fields="fields"
            :filter="filter"
            sort-by="extendedProps.treatmentPeriod"
            responsive
          >
            <template v-slot:cell(index)="row">{{ row.index + 1 }}</template>
            <!-- ชื่อคนไข้ -->
            <template #cell(patientName)="row">
              <span class="ql-patient-name">
                {{ row.item.extendedProps.patientName }}
              </span>
            </template>
            <!-- DN -->
            <template #cell(dn)="row">
              {{ row.item.extendedProps.dn
              }}<span v-if="row.item.extendedProps.existDn" class="ql-sub">
                /{{ row.item.extendedProps.existDn }}</span
              >
            </template>
            <!-- แพทย์ -->
            <template #cell(doctorId)="row">
              {{ row.item.extendedProps.doctorName }}
            </template>
            <!-- ห้อง -->
            <template #cell(resourceId)="row">
              {{ getRoomListBy(row.item.resourceId, "id").name }}
            </template>
            <!-- นัดเพื่อ + โน้ต : merged -->
            <template v-slot:cell(treatmentTitle)="row">
              <span
                class="narrow-spacing"
                v-html="row.item.extendedProps.treatmentTitle"
              ></span>
              <div v-if="row.item.extendedProps.note" class="ql-sub">
                <i class="fas fa-sticky-note"></i>
                {{ row.item.extendedProps.note }}
              </div>
            </template>
            <!-- แจ้งเตือน : flat badge / add chip -->
            <template #cell(rmdId)="row">
              <span v-if="printing">
                {{ row.value ? findReminderObj(row.value, "val").label : "" }}
              </span>
              <template v-else>
                <span
                  v-if="row.value"
                  class="ql-badge"
                  :style="{
                    'background-color': findReminderObj(row.value, 'val').desc,
                  }"
                  @click.stop="openReminderModal(row.item)"
                  >{{ findReminderObj(row.value, "val").label }}</span
                >
                <span
                  v-else
                  class="ql-chip-add report-export-skip"
                  @click.stop="openReminderModal(row.item)"
                  >+ เพิ่มแจ้งเตือน</span
                >
              </template>
            </template>
            <!-- สถานะ : flat badge -->
            <template v-slot:cell(statusId)="row">
              <span v-if="printing"
                >{{ findStatusName(row.item.extendedProps.statusId).label }}
              </span>
              <button
                v-else
                type="button"
                class="ql-badge ql-badge-btn"
                :style="{
                  'background-color': row.item.extendedProps.statusColor,
                }"
                @click.stop="openStatusModal(row.item)"
                :disabled="
                  inClinic(row.item) ||
                  !isAccessMenu([
                    'COUNTER',
                    'MANAGER',
                    'ASSISTANT',
                    'HEADCOUNTER',
                  ])
                "
              >
                {{ findStatusName(row.item.extendedProps.statusId).label }}
              </button>
            </template>
            <!-- action -->
            <template #cell(action)="row">
              <button
                type="button"
                class="ql-action-btn"
                title="ตัวเลือก"
                @click.stop="openActionModal(row.item)"
              >
                <i class="fas fa-ellipsis-h"></i>
              </button>
            </template>
          </b-table>
        </div>
      </div>
    </div>
    <CustomerModal
      ref="customerModal"
      @openProfileCustomerModal="openProfileCustomerModal"
    ></CustomerModal>
    <SearchCustomerModal
      ref="searchCustomerModal"
      @openProfileCustomerModal="openProfileCustomerModal"
      @openCustomerModal="openCustomerModal"
    ></SearchCustomerModal>
    <ProfileCustomerModal
      ref="profileCustomerModal"
      @openCustomerModal="openCustomerModal"
      @openEventModal="openEventModal"
    ></ProfileCustomerModal>
    <PaymentHistoryModal ref="paymentHistoryModal"></PaymentHistoryModal>
    <EventModal ref="eventModal" @goToDateTime="goToDateTime"></EventModal>
    <CheckinModal
      ref="checkinModal"
      @openCustomerModal="openCustomerModal"
    ></CheckinModal>
    <PaymentModal ref="paymentModal"></PaymentModal>
    <!-- <EmrModal ref="emrModal"></EmrModal> -->
    <ActionModal ref="actionModal"></ActionModal>
    <StatusModal ref="statusModal" @isLoading="isLoading = $event" />
    <ReminderModal ref="reminderModal" @isLoading="isLoading = $event" />
    <PdfExport ref="pdfExport" />

    <Dialog ref="Dialog"></Dialog>
    <Loading v-if="isLoading"></Loading>

    <Notification></Notification>
  </div>
</template>

<script>
import Vue from "vue";
import { mapGetters, mapActions } from "vuex";
import { eventBus } from "@/main";

import CustomerModal from "@/components/modal/Customer";
import SearchCustomerModal from "@/components/modal/SearchCustomer";
import ProfileCustomerModal from "@/components/modal/ProfileCustomer";
import PaymentHistoryModal from "@/components/modal/PaymentHistory";
import EventModal from "@/components/modal/Event";
import CheckinModal from "@/components/modal/Checkin";
import PaymentModal from "@/components/modal/Payment";
// import EmrModal from "@/components/modal/Emr";
import ActionModal from "@/components/modal/Action";
import StatusModal from "@/components/modal/Status";
import ReminderModal from "@/components/modal/Reminder";
import PdfExport from "@/components/PdfExport";

import Dialog from "@/components/modal/Dialog";
import Notification from "@/components/Notification";
import Loading from "@/components/Loading";

import moment from "moment";
import Multiselect from "vue-multiselect";
import { exportTableToExcel } from "@/utils/reportExport";

export default {
  name: "AppointmentList",
  components: {
    CustomerModal,
    SearchCustomerModal,
    ProfileCustomerModal,
    PaymentHistoryModal,
    EventModal,
    CheckinModal,
    StatusModal,
    Dialog,
    Notification,
    Loading,
    PaymentModal,
    // EmrModal,
    ActionModal,
    ReminderModal,
    PdfExport,
  },
  data() {
    return {
      isLoading: false,
      date: moment().format("YYYY-MM-DD"),
      fields: [
        {
          key: "index",
          label: "#",
          class: "ctr text-muted small-text report-export-skip",
          thClass: "report-export-skip",
        },
        {
          key: "extendedProps.treatmentPeriod",
          label: "เวลา",
          sortable: true,
          class: "narrow-spacing",
        },
        {
          key: "patientName",
          label: "ชื่อคนไข้",
          sortable: true,
          class: "narrow-spacing",
        },
        {
          key: "dn",
          label: "DN",
          class: "narrow-spacing",
        },
        {
          key: "extendedProps.patient.dob",
          label: "อายุ",
          class: "narrow-spacing",
          formatter: "calAge",
        },
        {
          key: "doctorId",
          label: "แพทย์",
          sortable: true,
          class: "narrow-spacing",
        },
        {
          key: "resourceId",
          label: "ห้อง",
          sortable: true,
          class: "narrow-spacing",
        },
        {
          key: "treatmentTitle",
          label: "นัดเพื่อ + โน้ต",
          class: "narrow-spacing",
          // sortable: true
        },
        {
          key: "extendedProps.phoneNo",
          label: "เบอร์โทร",
          class: "narrow-spacing",
          // Force text type on Excel export. A phone like "0812345678" is read
          // as a number, dropping the leading zero. data-t="s" keeps it text.
          tdAttr: { "data-t": "s" },
        },
        {
          key: "rmdId",
          label: "แจ้งเตือน",
          sortable: true,
          class: "narrow-spacing",
        },
        {
          key: "statusId",
          label: "สถานะ",
          sortable: true,
          class: "narrow-spacing",
        },
        {
          key: "action",
          label: "",
          class: "ctr report-export-skip",
          thClass: "report-export-skip",
        },
      ],
      filter: null,
      filterDoctor: null,
      filterRoom: null,
      filterStatus: null,
      filterReminder: null,
      isReadyWatch: false,
      fromTodo: false,
      printing: false,
    };
  },
  computed: {
    ...mapGetters({
      roomList: "moduleUser/getRoomList",
      doctorList: "moduleUser/getDoctorList",
      getRoomListBy: "moduleUser/getRoomListBy",
      branchInfo: "moduleUser/getBranchInfo",
      appointmentList: "moduleAppointment/getQueueAppointmentList",
      appointmentBy: "moduleAppointment/getQueueAppointmentBy",
      getAppointmentStatusList: "moduleMaster/getAppointmentStatusList",
      getReminderList: "moduleMaster/getReminderList",

      isAccessMenu: "moduleUser/getIsAccessMenu",
    }),
    dropdownStatus() {
      return this.getAppointmentStatusList.filter(
        (item) => item.code < 5 || item.code > 7
      );
    },
    filteredAppointmentList() {
      let filteredList = this.appointmentList
        .filter((i) => i.extendedProps.patient?.id) //filter note ออก
        .map((i) => {
          let o = Object.assign({}, i);
          o.statusId = i.extendedProps.statusId;
          o.rmdId = i.extendedProps.rmdId;
          o.doctorId = i.extendedProps.doctor;
          o.patientName = i.extendedProps.patientName;
          return o;
        });
      if (this.filterDoctor) {
        filteredList = filteredList.filter(
          (apt) => apt.extendedProps.doctor == this.filterDoctor
        );
      }
      if (this.filterRoom) {
        filteredList = filteredList.filter(
          (apt) => apt.resourceId == this.filterRoom
        );
      }
      if (this.filterStatus) {
        filteredList = filteredList.filter(
          (apt) => apt.statusId == this.filterStatus
        );
      }
      if (this.filterReminder) {
        if (this.filterReminder === "incompleted") {
          filteredList = filteredList.filter(
            (apt) => apt.rmdId > 0 && apt.rmdId <= 10
          );
        } else if (this.filterReminder === "completed") {
          filteredList = filteredList.filter((apt) => apt.rmdId > 10);
        }
      }

      return filteredList;
    },
    isEmptyAptList() {
      return this.appointmentList.length === 0 ? true : false;
    },
  },
  created() {
    this.init();
  },
  watch: {
    date: function (newDate) {
      if (this.isReadyWatch) {
        this.setStateQueueDate(newDate);
        this.getAppointmentListByDate(newDate);
      }
    },
  },
  methods: {
    ...mapActions({
      fetchQueueAppointmentList: "moduleAppointment/fetchQueueAppointmentList",
      moveQueueAppointment: "moduleAppointment/moveQueueAppointment",
      setStateQueueDate: "moduleAppointment/setStateQueueDate",
      confirmationQueueAppointment:
        "moduleAppointment/confirmationQueueAppointment",
      addRecentPatient: "modulePatient/addRecentPatient",
    }),
    init() {
      this.setStateQueueDate(this.date);
      this.getAppointmentListByDate(this.date);
      this.isReadyWatch = true;
    },
    getAppointmentListByDate(date, silent = false) {
      if (!silent) this.isLoading = true;
      if (!date) date = moment().format("YYYY-MM-DD");
      this.fetchQueueAppointmentList({
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
        date: date,
      })
        .then((res) => {
          this.isLoading = false;
        })
        .catch((err) => {
          this.isLoading = false;
        });
    },
    today(e) {
      this.date = moment().format("YYYY-MM-DD");
    },
    tomorrow() {
      this.date = moment().add(1, "day").format("YYYY-MM-DD");
    },
    yesterday() {
      this.date = moment().add(-1, "day").format("YYYY-MM-DD");
    },
    nextDay() {
      this.date = moment(this.date).add(1, "days").format("YYYY-MM-DD");
    },
    prevDay() {
      this.date = moment(this.date).add(-1, "days").format("YYYY-MM-DD");
    },
    changeApptStatus(apt, statusId) {
      this.isLoading = true;
      this.confirmationQueueAppointment({
        statusId: { statusId: statusId },
        id: apt.id,
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
      })
        .then((res) => {
          if (res.status == "200") {
            var index = this.appointmentList.findIndex(function (event) {
              return event.id == res.data.id;
            });
            if (index >= 0) {
              Object.assign(this.appointmentList[index], res.data);
            }
          }
          this.isLoading = false;
        })
        .catch(() => {
          this.isLoading = false;
          this.$parent.showDialogToast("warning", "ไม่สามารถเปลี่ยนสถานะได้");
        });
    },
    findStatusName(statusId) {
      return (
        this.getAppointmentStatusList.find(function (item) {
          return item["code"] == statusId;
        }) || {}
      );
    },
    findReminderObj(rmdId) {
      return (
        this.getReminderList.find(function (item) {
          return item["val"] == rmdId;
        }) || {}
      );
    },

    //open Modal components
    openCustomerModal(patient, appointment = null, readOnly) {
      if (!appointment && this.lastFcResourceSelected) {
        appointment = this.getAppointmentFormat(this.lastFcResourceSelected);
      }
      this.$nextTick().then((res) => {
        if (this.$refs.customerModal) {
          this.$refs.customerModal.show(patient, appointment, readOnly);
        }
      });
    },
    openSearchCustomerModal() {
      const appointment = this.getAppointmentFormat(
        this.lastFcResourceSelected
      );
      this.$nextTick().then((res) => {
        if (this.$refs.searchCustomerModal) {
          this.$refs.searchCustomerModal.show(appointment);
        }
      });
    },
    openProfileCustomerModal(dataToEvent, patient, hideFooter, mode) {
      this.$refs.profileCustomerModal.show(
        dataToEvent,
        patient,
        hideFooter,
        mode
      );
    },
    openPaymentHistoryModal(appointment, patient) {
      this.$refs.paymentHistoryModal.show(appointment, patient);
    },
    openEventModal(dataToEvent, patient) {
      this.$refs.eventModal.show(dataToEvent, patient);
    },
    openConfirmationModal() {
      this.$refs.confirmationModal.show();
    },
    // openEmrModal(patient, drawObj, readOnly) {
    //   this.$refs.emrModal.show(patient, drawObj, readOnly);
    // },
    openNextVisitModal(patient) {
      this.$refs.nextVisitModal.show(patient);
    },
    openPaymentModal(data, index) {
      this.$refs.paymentModal.show(data);
    },
    openActionModal(item, index) {
      this.addRecentPatient(item.extendedProps.patient);
      this.$refs.actionModal.show(item);
    },
    openCheckInModal(index, appointment, patient) {
      this.$refs.checkinModal.show(index, appointment, patient);
    },
    openStatusModal(appointment) {
      this.$refs.statusModal.show(appointment);
    },
    openReminderModal(appointment) {
      this.$refs.reminderModal.show(appointment);
    },
    cancelCheckIn(index) {
      this.$refs.Dialog.showAlertConfirm(
        "ยกเลิกส่งตรวจ ?",
        "คุณต้องการยกเลิกส่งตรวจ",
        "warning",
        "ใช่",
        "ไม่"
      ).then((res) => {
        if (res.isConfirmed) {
          this.isLoading = true;
          this.$refs.checkinModal
            .cancelCheckin(index)
            .then(() => {
              this.showDialogToast("success", "ยกเลิกส่งตรวจสำเร็จ");
            })
            .catch(() => {
              this.showDialogToast("warning", "ยกเลิกส่งตรวจไม่สำเร็จ");
            })
            .finally(() => {
              this.isLoading = false;
            });
        }
      });
    },

    //Utils
    showDialogToast(variant, textDetail, noAutoHide) {
      this.$refs.Dialog.showToast(variant, textDetail, noAutoHide);
    },
    goToDateTime(dateTime) {
      let date = moment(dateTime).format("YYYY-MM-DD");
      // let time = moment(dateTime).format("HH:mm:ss");
      this.date = date;
    },
    inClinic(appointment) {
      if (appointment) {
        return appointment.extendedProps.statusId == 5 ||
          appointment.extendedProps.statusId == 6 ||
          appointment.extendedProps.statusId == 7
          ? true
          : false;
      } else {
        return false;
      }
    },
    isSameDate(dayAdd) {
      return moment(this.date).isSame(moment().add(dayAdd, "day"), "day");
    },
    async generatePdf(el) {
      this.printing = true;
      await this.$nextTick();
      await this.$refs.pdfExport.generatePdf({
        header: this.$refs.header,
        body: this.$refs[el].$el,
        footer: this.$refs.footer,
        option: {
          orientation: "landscape",
          filename: "appointment_" + this.date,
          // fontSize: "10px",
        },
      });
      this.printing = false;
    },
    exportExcel() {
      const root = this.$refs.queueListTable && this.$refs.queueListTable.$el;
      if (!root) return;
      const tableEl = root.querySelector("table") || root;
      try {
        exportTableToExcel({
          tableEl,
          reportTitle: "ตารางนัดประจำวัน",
          reportNameEn: "appointment",
          dateFrom: this.date,
          dateTo: this.date,
          branchName: this.branchInfo && this.branchInfo.name,
          clinicName: this.branchInfo && this.branchInfo.clinicName,
        });
      } catch (err) {
        console.error(err);
        this.showDialogToast("warning", "ไม่สามารถส่งออกไฟล์ Excel ได้");
      }
    },
    defaultValue() {
      this.filter = null;
      this.filterDoctor = null;
      this.filterRoom = null;
      this.filterStatus = null;
      this.filterReminder = null;
    },
    async createEmptyTreatment(appointment) {
      let treatment = {
        // id: null,
        appoinmentId: appointment.id,
        items: [],
      };
      await this.$store
        .dispatch("moduleTreatment/createTreatment", {
          clinicUrl: this.$route.params.clinicUrl,
          branchUrl: this.$route.params.branchUrl,
          event: treatment,
        })
        .then((res) => {
          // if (res.status == 200) {
          // }
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    formatDate(val) {
      this.date = moment(val).format("YYYY-MM-DD");
    },
    calAge(dateString) {
      if (!dateString) return;
      const date = moment(dateString, "YYYY-MM-DD");
      return moment().diff(date, "years");
    },
  },
  mounted() {
    eventBus.$on("openCustomerModal", () => {
      this.$refs.customerModal.show();
    });
    eventBus.$on("openSearchCustomerModal", () => {
      this.$refs.searchCustomerModal.show();
    });
    eventBus.$on("openCheckInModal", (appointment) => {
      this.openCheckInModal(null, appointment);
    });
    eventBus.$on("openPaymentHistoryModal", (appointment) => {
      this.openPaymentHistoryModal(appointment);
    });
    eventBus.$on("openPaymentModal", async (appointment) => {
      await this.createEmptyTreatment(appointment);
      this.openPaymentModal(appointment);
    });
    eventBus.$on("todoClick", (arg) => {
      this.fromTodo = true;
      this.defaultValue();
      this.tomorrow();
      // if (arg === "status") {
      //   this.filterStatus = 1;
      // } else if (arg === "reminder") {
      //   this.filterReminder = "incompleted";
      // }
    });
    if (!this.fromTodo) {
      this.today();
    }
    eventBus.$on("fetch-queue-data", async () => {
      await this.$nextTick();
      this.getAppointmentListByDate(this.date, true);
    });
  },

  updated() {},
  destroyed() {
    eventBus.$off("openCustomerModal");
    eventBus.$off("openSearchCustomerModal");
    eventBus.$off("openCheckInModal");
    eventBus.$off("openPaymentHistoryModal");
    eventBus.$off("openPaymentModal");
    eventBus.$off("todoClick");
    eventBus.$off("fetch-queue-data");
  },
};
</script>

<style scoped>
/* Design tokens mirrored from the DF Correction page for visual consistency */
.ql-page {
  --card: #ffffff;
  --ink: #0f1b2d;
  --ink-2: #2a3950;
  --muted: #6b7a90;
  --muted-2: #8a96a8;
  --line: #e3e8ef;
  --line-2: #eef1f6;
  --primary: #2563eb;
  --primary-600: #1d4ed8;
  --primary-50: #eff4ff;
  --chip-bg: #f1f4f9;
  --shadow-sm: 0 1px 2px rgba(15, 23, 42, 0.04);
  color: var(--ink);
  padding: 18px 22px 48px;
}

/* Card */
.ql-card {
  background: var(--card);
  border: 1px solid var(--line);
  border-radius: 10px;
  box-shadow: var(--shadow-sm);
  overflow: hidden;
}

/* Filter bar */
.filter-bar {
  padding: 14px 16px;
  border-bottom: 1px solid var(--line);
  background: linear-gradient(180deg, #fafbfd, #ffffff);
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.filter-grid {
  display: grid;
  gap: 10px 14px;
  align-items: end;
}
.filter-grid-top {
  grid-template-columns: minmax(260px, auto) auto minmax(200px, 1fr) auto;
}
.filter-grid-filters {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}
@media (max-width: 991px) {
  .filter-grid-top,
  .filter-grid-filters {
    grid-template-columns: 1fr 1fr;
  }
}

.field {
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
}
.field > label {
  font-size: 11.5px;
  font-weight: 500;
  color: var(--muted);
  letter-spacing: 0.01em;
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
}
.field > label i {
  font-size: 10px;
  opacity: 0.7;
}
.field.actions {
  flex-direction: column;
  justify-content: flex-end;
}

/* Inputs */
.ctrl {
  height: 36px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: #fff;
  padding: 0 12px;
  font: inherit;
  font-size: 13.5px;
  color: var(--ink);
  outline: none;
  transition: border-color 0.12s, box-shadow 0.12s;
  width: 100%;
}
.ctrl::placeholder {
  color: var(--muted-2);
}
.ctrl:hover {
  border-color: #cdd5e0;
}
.ctrl:focus {
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
}
.ctrl-wrap {
  position: relative;
}
.ctrl-wrap > i.lead {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--muted);
  font-size: 12px;
  pointer-events: none;
  z-index: 1;
}
.ctrl-wrap > i.lead + .ctrl {
  padding-left: 32px;
}
.ctrl-wrap > .trail {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--muted);
  font-size: 12px;
}
.ctrl-wrap > .trail.clearable {
  cursor: pointer;
  pointer-events: auto;
}
.select-wrap::after {
  content: "\f078";
  font-family: "Font Awesome 5 Free";
  font-weight: 900;
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--muted);
  font-size: 10px;
  pointer-events: none;
}
select.ctrl {
  appearance: none;
  -webkit-appearance: none;
  background: #fff;
  padding-right: 32px;
}

/* Date row: prev / picker / next */
.ql-date-row {
  display: flex;
  align-items: center;
  gap: 6px;
}
.ql-date-row .date-wrap {
  position: relative;
  display: flex;
  align-items: center;
  height: 36px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: #fff;
  flex: 1;
  min-width: 150px;
}
.ql-date-row .date-wrap:focus-within {
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
}
.ql-date-row >>> .ql-date-input {
  border: 0 !important;
  box-shadow: none !important;
  outline: none !important;
  background: transparent !important;
  height: 34px;
  font-size: 13.5px;
  color: var(--ink);
  text-align: center;
  width: 100%;
  cursor: pointer;
}
.icon-btn {
  height: 36px;
  width: 32px;
  flex: none;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: #fff;
  color: var(--ink-2);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  transition: background 0.12s, border-color 0.12s;
}
.icon-btn:hover {
  background: var(--line-2);
  border-color: #cdd5e0;
}

/* Quick day chips */
.quick-chips {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 36px;
}
.chip {
  height: 30px;
  padding: 0 12px;
  border-radius: 999px;
  border: 1px solid var(--line);
  background: #fff;
  font-size: 12.5px;
  color: var(--ink-2);
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
  transition: background 0.12s, border-color 0.12s, color 0.12s;
}
.chip:hover {
  background: var(--line-2);
}
.chip.active {
  background: var(--primary-50);
  color: var(--primary-600);
  border-color: #b9cef9;
  font-weight: 600;
}

/* Buttons */
.btn-xs {
  height: 28px;
  padding: 0 10px;
  font-size: 12.5px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

/* Table toolbar */
.table-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 9px 16px;
  border-bottom: 1px solid var(--line);
  gap: 12px;
}
.table-toolbar .left {
  font-size: 13px;
  color: var(--muted);
}
.table-toolbar .left strong {
  color: var(--ink);
  font-weight: 600;
}

/* Table */
.tbl-wrap {
  overflow: hidden;
}
.ql-tbl >>> table.b-table {
  margin: 0;
  font-size: 13px;
}
.ql-tbl >>> thead th {
  background: #f7f9fc !important;
  color: var(--muted);
  font-weight: 500;
  font-size: 11.5px;
  text-align: left;
  padding: 9px 10px;
  border-bottom: 1px solid var(--line);
  border-top: 0;
  white-space: nowrap;
  letter-spacing: 0.03em;
}
.ql-tbl >>> tbody td {
  padding: 9px 10px;
  border-bottom: 1px solid var(--line-2);
  border-top: 0;
  color: var(--ink-2);
  vertical-align: middle;
}
.ql-tbl >>> tbody tr {
  cursor: default;
}
.ql-tbl >>> tbody tr:hover td {
  background: #fafbfd;
}
.ql-action-btn {
  border: none;
  background: transparent;
  color: var(--muted-2);
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 13px;
  transition: background 0.12s, color 0.12s;
}
.ql-action-btn:hover {
  background: var(--primary-50);
  color: var(--primary-600);
}

/* Cell helpers */
.ql-patient-name {
  font-weight: 600;
  color: var(--ink);
}
.ql-sub {
  color: var(--muted);
  font-size: 11.5px;
  display: flex;
  align-items: baseline;
  gap: 4px;
}
.ql-sub i {
  font-size: 9px;
  opacity: 0.7;
}

/* Status / reminder pill badges */
.ql-badge {
  display: inline-flex;
  align-items: center;
  border: none;
  border-radius: 999px;
  padding: 3px 10px;
  font-size: 11.5px;
  font-weight: 500;
  line-height: 1.4;
  color: #fff;
  cursor: pointer;
  white-space: nowrap;
}
.ql-badge:hover {
  filter: brightness(0.96);
}
.ql-badge-btn:disabled {
  cursor: default;
  opacity: 0.7;
  filter: none;
}
.ql-chip-add {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 1px dashed #cbd2dc;
  border-radius: 999px;
  padding: 2px 9px;
  font-size: 11px;
  color: var(--muted-2);
  background: transparent;
  cursor: pointer;
  white-space: nowrap;
}
.ql-chip-add:hover {
  border-color: var(--primary);
  color: var(--primary-600);
}
</style>